import {
  FilterQuery,
  SaF,
  SaFLegend,
  SkeletonDots
} from '@yes.technology/react-toolkit'
import { useEffect, useId } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSitemodel } from 'sitemodel/shared/hooks'
import { useSiteState } from 'siteState/shared'
import AppliedFilters, {
  AppliedFiltersProps
} from '../AppliedFilters/AppliedFilters'
import SectionDivider from '../SectionDivider'
import { LegendsContainer } from './SafChartFilter.styles'
import useSaFQuery from './useSaFQuery'

export type SaFChartFilterProps = {
  props: {
    static_input?: React.ComponentProps<typeof SaF>
    static_dates?: AppliedFiltersProps
  }
}

type SaFProps = React.ComponentProps<typeof SaF>
type ErrorType = SaFProps['error']

export default function SaFChartFilter({
  props: { static_input: staticInput, static_dates }
}: SaFChartFilterProps) {
  const sitemodel = useSitemodel()

  const title = sitemodel.title || ''

  const sectionId = useId()

  const [filterQuery] = useSiteState<FilterQuery | undefined>('filter-query')

  const [objectclassUuidInState] = useSiteState<string | undefined>(
    'objectclassuuid'
  )
  const [searchParams] = useSearchParams()
  const objectclassUuid =
    searchParams.get('uuid_objectclass') || objectclassUuidInState || ''

  const {
    data,
    error: requestError,
    isFetching
  } = useSaFQuery({
    filterQuery: staticInput ? undefined : filterQuery,
    objectclassUuid
  })

  const input = staticInput || data

  const emptyNodesError =
    input && input.data.nodes.length < 1
      ? new Error('SaF data for nodes is empty')
      : null

  const error = requestError || emptyNodesError

  const getErrorType = () => {
    let errorType: ErrorType
    if (emptyNodesError) {
      errorType = 'no-data'
    } else if (requestError || !input || data?.success === false) {
      errorType = 'invalid-data'
    }

    return errorType
  }

  useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  const errorType = getErrorType()

  if (isFetching) {
    return <SkeletonDots size={12} className='justify-content-center' />
  }

  if (!input && !isFetching && !data && !error) {
    return null
  }

  return (
    <SectionDivider
      id={sectionId}
      title={title}
      centerHeaderElement={<>SaF Chart</>}
      backgroundColor='#004099'
    >
      {input || errorType ? (
        <>
          <SaF {...(input as SaFProps)} error={errorType} />
          <LegendsContainer>
            <SaFLegend />
            <AppliedFilters {...static_dates} />
          </LegendsContainer>
        </>
      ) : (
        <SkeletonDots size={12} className='justify-content-center' />
      )}
    </SectionDivider>
  )
}
